import { api } from './client'

export type Formalidade = 'Informal' | 'Cordial' | 'Formal'
export type Brevidade = 'Curta' | 'Media' | 'Detalhada'

/**
 * O que o assistente é na operação (demanda 052). Substituiu o antigo booleano
 * `interno`, que não sabia dizer o que é o Copiloto: ele não tem canal e só
 * escreve nota privada.
 */
export type PapelDoAssistente = 'Atendimento' | 'Interno' | 'Copiloto'

/** A situação do token de autoria — nunca o token. */
export type SituacaoDoToken = {
  proprio: boolean
  origem: string | null
  ultimosQuatro: string | null
}

export type ConferenciaDoToken = {
  conferido: boolean
  dono?: string | null
  idNaPlataforma?: number | null
  erro?: string | null
}

export type AssistenteNaLista = {
  id: number
  nome: string
  marca: string
  /** Canais atendidos. Um assistente serve vários. */
  caixas: string[]
  papel: PapelDoAssistente
  ativo: boolean
  provedor: string
  modelo: string
}

export type Assistente = {
  id: number
  nome: string
  papel: PapelDoAssistente
  ativo: boolean
  caixasDeEntradaIds: number[]
  persona: string
  formalidade: Formalidade
  brevidade: Brevidade
  instrucoes: string
  avisoDeAutomacao: string
  setorDeEscalonamento: string | null

  /** O que o resumo do painel de contexto destaca (041). Nulo = padrão de fábrica. */
  destaquesDoResumo: string | null
  provedor: string
  modelo: string

  /**
   * Variedade da redação, 0,0 a 1,0 (demanda 133).
   *
   * ⚠️ Não é o botão da fluidez: sobe a variedade **e** o risco de invenção.
   */
  temperatura: number
  maximoDeTokens: number
}

/** O prompt final, com as regras que originaram cada frase. */
export type PreviaDoPrompt = {
  prompt: string
  caracteres: number

  /** Versão registrada no momento da leitura — `P-07`. */
  versao: number | null
  regras: { chave: string; valor: string }[]
}

export type VersaoDePromptNaLista = {
  numero: number
  impressao: string
  origem: 'AlteracaoDoAssistente' | 'AlteracaoDeParametro' | 'Detectada'
  geradoEm: string

  /** Nulo quando a diferença foi apenas notada — atribuir autor seria registro falso. */
  geradoPor: string | null
  caracteres: number
}

export type LinhaDaDiferenca = { marca: 'igual' | 'entrou' | 'saiu'; texto: string }

export type Diferenca = {
  de: number
  ate: number | null
  entraram: number
  sairam: number
  linhas: LinhaDaDiferenca[]
}

export type MarcaNaLista = {
  id: number
  chave: string
  nome: string
  telefone: string | null
  ativa: boolean
  caixas: number
}

export type PedidoDeCaixa = {
  idExterno: number
  nome: string
  tipoDeIdentificador: string
  canalExterno: string | null
  ativa: boolean
}

export type CaixaDoChatwoot = {
  id: number
  nome: string
  canal: string | null

  /** Já ligada a alguma marca — dizer aqui evita o 409 depois de preencher tudo. */
  jaCadastrada: boolean
}

export type CaixasNoChatwoot = {
  /** Indisponibilidade do Chatwoot não é erro do usuário: a tela segue deixando digitar. */
  erro: string | null
  caixas: CaixaDoChatwoot[]
}

export type CaixaNaLista = {
  id: number
  idExterno: number
  nome: string
  tipoDeIdentificador: string
  canalExterno: string | null
  ativa: boolean

  /** Quem já ocupa a caixa. Cada canal atende um assistente só. */
  assistente: string | null
}

export type PedidoDeAssistente = {
  nome: string
  caixasDeEntradaIds: number[]
  papel: PapelDoAssistente
  ativo: boolean
  persona: string
  formalidade: Formalidade
  brevidade: Brevidade
  instrucoes: string
  avisoDeAutomacao: string
  setorDeEscalonamento: string | null
  destaquesDoResumo: string | null

  /** Demanda 133. Ausente mantém o que está gravado — nunca zera. */
  temperatura?: number
  maximoDeTokens?: number
}

export const marcas = {
  listar: () => api.get<MarcaNaLista[]>('/marcas'),
  criar: (nome: string, telefone: string) =>
    api.post<{ id: number; chave: string; nome: string }>('/marcas', {
      nome, chave: null, telefone, ativa: true,
    }),

  caixas: (marca: string) => api.get<CaixaNaLista[]>(`/marcas/${marca}/caixas`),

  criarCaixa: (
    marca: string,
    idExterno: number,
    nome: string,
    tipoDeIdentificador: string,
    canalExterno: string,
  ) =>
    api.post<{ id: number; idExterno: number; nome: string }>(`/marcas/${marca}/caixas`, {
      idExterno, nome, tipoDeIdentificador, canalExterno, ativa: true,
    }),

  /**
   * Altera a caixa — inclusive o `idExterno`.
   *
   * 🔴 É por esse número que o webhook descobre de quem é a mensagem. Errá-lo
   * faz todo evento cair em `Descartado`, sem sintoma nenhum do lado do portal.
   */
  alterarCaixa: (marca: string, id: number, pedido: PedidoDeCaixa) =>
    api.put<void>(`/marcas/${marca}/caixas/${id}`, pedido),

  /** As caixas que existem do lado do Chatwoot, para conferir sem digitar. */
  caixasNoChatwoot: (marca: string) =>
    api.get<CaixasNoChatwoot>(`/marcas/${marca}/caixas/no-chatwoot`),
}

export const assistentes = {
  criar: (marca: string, pedido: PedidoDeAssistente) =>
    api.post<{ id: number; nome: string }>(`/marcas/${marca}/assistentes`, pedido),

  listar: (marca: string) => api.get<AssistenteNaLista[]>(`/marcas/${marca}/assistentes`),
  ler: (marca: string, id: number) => api.get<Assistente>(`/marcas/${marca}/assistentes/${id}`),
  prompt: (marca: string, id: number) =>
    api.get<PreviaDoPrompt>(`/marcas/${marca}/assistentes/${id}/prompt`),
  salvar: (marca: string, id: number, dados: Omit<Assistente, 'id' | 'provedor' | 'modelo'>) =>
    api.put<void>(`/marcas/${marca}/assistentes/${id}`, dados),

  versoesDoPrompt: (marca: string, id: number) =>
    api.get<VersaoDePromptNaLista[]>(`/marcas/${marca}/assistentes/${id}/prompt/versoes`),

  /** Sem `ate`, compara com o prompt de agora — a pergunta mais frequente. */
  diferenca: (marca: string, id: number, de?: number, ate?: number) => {
    const p = new URLSearchParams()
    if (de !== undefined) p.set('de', String(de))
    if (ate !== undefined) p.set('ate', String(ate))

    return api.get<Diferenca>(`/marcas/${marca}/assistentes/${id}/prompt/diferenca?${p}`)
  },

  // --- identidade na plataforma de atendimento (demanda 052) ---------------

  /** A situação do token. Devolve os quatro últimos, nunca o valor. */
  situacaoDoToken: (marca: string, id: number) =>
    api.get<SituacaoDoToken>(`/marcas/${marca}/assistentes/${id}/token`),

  /**
   * Grava o token de autoria.
   *
   * 🔴 Só chame com o campo preenchido: mandar vazio é erro de validação, de
   * propósito — a tela nunca recebe o token atual, então salvar em branco não
   * pode apagá-lo. Para remover existe o `apagarToken`.
   */
  gravarToken: (marca: string, id: number, token: string) =>
    api.put<SituacaoDoToken>(`/marcas/${marca}/assistentes/${id}/token`, { token }),

  apagarToken: (marca: string, id: number) =>
    api.delete<SituacaoDoToken>(`/marcas/${marca}/assistentes/${id}/token`),

  /** De quem é o token — confere antes de gravar. Sem `token`, confere o gravado. */
  conferirToken: (marca: string, id: number, token?: string) =>
    api.post<ConferenciaDoToken>(`/marcas/${marca}/assistentes/${id}/token/conferir`, { token }),
}
